import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useAuthStore } from './auth';
import { canOperateAccounts } from '@/lib/plain';

// Simple vs Advanced view (2026-07). The account operators (Profile Admins) are
// low-literacy IT users; Simple mode gives them a task-first, plain-language
// workspace (add / find & update / remove a person) and hides the dense
// power-user screens. Advanced mode is the full app. The choice is persisted per
// browser; operators default to Simple, everyone else to Advanced.

export type UiMode = 'simple' | 'advanced';

const STORAGE_KEY = 'af-ui-mode';

export const useUiModeStore = defineStore('uiMode', () => {
  const auth = useAuthStore();

  // Null until the user has expressed a preference; before that the mode is
  // derived from their roles so a first-time operator lands in Simple.
  const override = ref<UiMode | null>(
    (localStorage.getItem(STORAGE_KEY) as UiMode | null) ?? null,
  );

  // Simple mode only makes sense for people who can actually add/update/remove
  // accounts. For everyone else (auditors, help desk, template authors) there is
  // no simple surface, so the toggle is hidden and the mode is always advanced.
  const canUseSimple = computed(() => canOperateAccounts(auth.roles));

  // Default when the user hasn't chosen: an operator who is not a full Admin
  // starts simple; Admins and non-operators start advanced.
  const defaultMode = computed<UiMode>(() =>
    canUseSimple.value && !auth.hasRole('Admin') ? 'simple' : 'advanced',
  );

  const mode = computed<UiMode>(() => {
    if (!canUseSimple.value) return 'advanced';
    return override.value ?? defaultMode.value;
  });

  const isSimple = computed(() => mode.value === 'simple');

  function setMode(next: UiMode): void {
    override.value = next;
    localStorage.setItem(STORAGE_KEY, next);
  }

  function toggle(): void {
    setMode(isSimple.value ? 'advanced' : 'simple');
  }

  // Note: we deliberately never clear the stored preference when canUseSimple
  // goes false (e.g. roles emptied on sign-out) - that would wipe the user's own
  // choice on every sign-out. `mode` already forces 'advanced' for anyone who
  // can't use simple, so a lingering preference for a non-operator is harmless.

  return { mode, isSimple, canUseSimple, defaultMode, setMode, toggle };
});
